import { useState } from 'react';
import { useAlert } from 'react-alert';
import { useSelector } from 'react-redux';
import I18n from 'i18n-js';

import { requestPasswordReset } from '@lootboy/lib/actions/AuthActions';
import {
  EForgotPasswordField,
  useForgotPasswordForm,
} from '@lootboy/lib/forms/useForgotPasswordForm';

import Alert from '~/components/Alert';
import Input from '~/components/atoms/FormControls/Input';
import StyledButton, {
  EStyledButtonVariant,
} from '~/components/atoms/StyledButton';
import { MenuViewHeader } from '~/components/MenuViewHeader';
import { AuthViewHeadline } from '~/components/molecules/AuthViewHeadline';
import { CaptchaOverlay } from '~/components/molecules/CaptchaOverlay';

import useBooleanState from '~/hooks/useBooleanState';
import { previousPathSelector } from '~/redux/slices/location/selectors';
import { useAppDispatch } from '~/redux/store/useAppDispatch';
import {
  LOGIN_PATH,
  PASSWORD_RESET_CODE_PATH,
  REQUEST_PASSWORD_RESET_PATH,
} from '~/routes/paths';
import { useAppHistory, useAppLocation } from '~/utilities/routing';

import styles from './RequestPasswordReset.module.scss';

const REQUEST_PASSWORD_RESET_VIEW_FORM_ID = 'request_password_reset_form';

export function RequestPasswordResetPopup(): JSX.Element {
  const alert = useAlert();
  const dispatch = useAppDispatch();
  const history = useAppHistory();
  const { state } = useAppLocation<typeof REQUEST_PASSWORD_RESET_PATH>();
  const previousPath = useSelector(previousPathSelector);
  const [email, setEmail] = useState<string | null>(null);
  const [isCaptchaShown, { setTrue: showCaptcha, setFalse: hideCaptcha }] =
    useBooleanState();

  const {
    formState: { errors, isValid, isSubmitting },
    onFormSubmit,
    register,
  } = useForgotPasswordForm(
    (values) => {
      setEmail(values.email);
      showCaptcha();
    },
    { defaultValues: { [EForgotPasswordField.EMAIL]: state?.email ?? '' } },
  );

  const onCaptchaChange = async (key: string) => {
    if (!email) {
      return;
    }
    try {
      await dispatch(requestPasswordReset(email, key));
      history.push(PASSWORD_RESET_CODE_PATH, { email });
    } catch (error) {
      alert.error(
        <Alert
          title={I18n.t('ResetPasswordView_request_code_failed')}
          body={(error as { message?: string })?.message ?? ''}
        />,
      );
      hideCaptcha();
    }
  };

  return (
    <CaptchaOverlay
      showCaptcha={isCaptchaShown}
      dismissCaptcha={hideCaptcha}
      onChange={onCaptchaChange}
    >
      <MenuViewHeader
        title={I18n.t('MenuView_login_bc')}
        onBack={() => {
          if (previousPath === LOGIN_PATH) {
            history.goBack();
          } else {
            history.push(LOGIN_PATH);
          }
        }}
      />
      <section className={styles.container}>
        <AuthViewHeadline
          headline={I18n.t('MenuView_reset_headline')}
          subHeadline={I18n.t('MenuView_reset_sub')}
        />
        <form
          onSubmit={onFormSubmit}
          className={styles.form}
          id={REQUEST_PASSWORD_RESET_VIEW_FORM_ID}
        >
          <Input
            {...register(EForgotPasswordField.EMAIL)}
            label={I18n.t('MenuView_email')}
            placeholder={I18n.t('MenuView_email_placeholder')}
            id={EForgotPasswordField.EMAIL}
            error={errors[EForgotPasswordField.EMAIL]?.message}
          />
        </form>
      </section>
      <footer className={styles.footer}>
        <StyledButton
          disabled={!isValid || isSubmitting}
          variant={EStyledButtonVariant.BLUE}
          className={styles.footerButton}
          form={REQUEST_PASSWORD_RESET_VIEW_FORM_ID}
          type="submit"
        >
          {I18n.t('confirm')}
        </StyledButton>
      </footer>
    </CaptchaOverlay>
  );
}
